import React, { useState } from 'react';
import { Button, useToast } from '@chakra-ui/react';
import { FaMicrosoft } from 'react-icons/fa';
import { PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { handleSuccessfulAuth } from '../utils/auth';

const MicrosoftSignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // MSAL configuration
  const msalConfig = {
    auth: {
      clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID || '',
      authority: 'https://login.microsoftonline.com/common', // Multi-tenant
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    }
  };

  const loginRequest = {
    scopes: ['openid', 'profile', 'email'],
    prompt: 'select_account'
  };

  const handleMicrosoftSignIn = async () => {
    if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID) {
      toast({
        title: 'Configuration Error',
        description: 'Microsoft Sign-In is not configured. Please contact support.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Initialize MSAL instance
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      // Attempt popup login
      const response = await msalInstance.loginPopup(loginRequest);

      if (response.accessToken) {
        // Send the Microsoft token to your backend for verification and user creation/login
        const backendResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/microsoft`,
          {
            token: response.accessToken,
            idToken: response.idToken,
            account: {
              username: response.account.username,
              name: response.account.name,
              localAccountId: response.account.localAccountId,
            }
          },
          { 
            withCredentials: true,
            timeout: 10000
          }
        );

        if (backendResponse?.data?.accessToken) {
          // Use the enhanced auth utility function
          await handleSuccessfulAuth(backendResponse.data, navigate, toast);
        } else {
          throw new Error('No access token received from server');
        }
      } else {
        throw new Error('No access token received from Microsoft');
      }
    } catch (error) {
      console.error('Microsoft Sign-In Error:', error);
      
      let errorMessage = 'Failed to sign in with Microsoft';
      
      if (error.errorCode === 'user_cancelled') {
        // User cancelled the login, don't show error
        return;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.errorMessage) {
        errorMessage = error.errorMessage;
      }
      
      toast({
        title: 'Sign-In Failed',
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID) {
    return (
      <Button
        leftIcon={<FaMicrosoft />}
        colorScheme="blue"
        variant="outline"
        width="full"
        isDisabled={true}
        _disabled={{
          opacity: 0.6,
          cursor: 'not-allowed',
        }}
      >
        Microsoft Sign-In Not Configured
      </Button>
    );
  }

  return (
    <Button
      leftIcon={<FaMicrosoft />}
      colorScheme="blue"
      variant="outline"
      width="full"
      onClick={handleMicrosoftSignIn}
      isLoading={isLoading}
      isDisabled={isLoading}
      loadingText="Signing in..."
      _hover={{
        bg: 'blue.50',
        borderColor: 'blue.300',
      }}
      _disabled={{
        opacity: 0.6,
        cursor: 'not-allowed',
      }}
    >
      Sign in with Microsoft
    </Button>
  );
};

export default MicrosoftSignIn;
